import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import DisclaimerBanner from '../components/DisclaimerBanner';
import PredictionPaymentModal from '../components/PredictionPaymentModal';
import { PredictionResultCard, AnalysisCards, MetricsGrid } from '../components/super-ia/PredictionResult';
import { PredictionHistoryList } from '../components/super-ia/PredictionHistory';
import { toast } from 'sonner';
import { 
  Brain, Zap, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';

const SuperIA = () => {
  const { token } = useAuth();
  const [lotteries, setLotteries] = useState([]);
  const [selectedLottery, setSelectedLottery] = useState('');
  const [periodDays, setPeriodDays] = useState('30');
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  const fetchLotteries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lotteries`);
      setLotteries(res.data);
      if (res.data.length > 0) {
        setSelectedLottery(res.data[0].id);
      }
    } catch (e) {
      toast.error('Error al cargar loterías');
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/super-ia/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.predictions || []);
    } catch (e) {
      console.warn('Error fetching history:', e.message);
    } finally {
      setLoadingHistory(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLotteries();
    fetchHistory();
  }, [fetchLotteries, fetchHistory]);

  const generatePrediction = async () => {
    if (!selectedLottery) {
      toast.error('Selecciona una lotería');
      return;
    }

    setLoading(true);
    setPrediction(null);

    try {
      const res = await axios.post(
        `${API}/super-ia/predict`,
        {
          lottery_id: selectedLottery,
          period_days: parseInt(periodDays)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPrediction(res.data);
      fetchHistory(); // Refresh history
      toast.success('¡Predicción generada con Sócrates!');
    } catch (e) {
      if (e.response?.status === 402) {
        setShowPayment(true);
      } else {
        toast.error(e.response?.data?.detail || 'Error al generar predicción');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedLotteryData = lotteries.find(l => l.id === selectedLottery);

  return (
    <div className="min-h-screen bg-slate-950 pb-20">
      <Navbar />
      
      <main className="pt-20 px-4 max-w-7xl mx-auto">
        {/* Hero Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 mb-4 animate-pulse">
            <Brain size={40} className="text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="super-ia-title">
            Sócrates
            <span className="ml-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              Predictor
            </span>
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Análisis avanzado con <span className="text-purple-400 font-semibold">Sócrates</span> + 
            algoritmos estadísticos para generar las predicciones más inteligentes
          </p>
        </div>

        {/* Configuration Card */}
        <Card className="glass-card border-purple-500/30 mb-6" data-testid="prediction-config">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              {/* Lottery Selector */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Selecciona Lotería</label>
                <Select value={selectedLottery} onValueChange={setSelectedLottery}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="lottery-select">
                    <SelectValue placeholder="Elige una lotería" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 max-h-64">
                    {lotteries.map(lottery => (
                      <SelectItem 
                        key={lottery.id} 
                        value={lottery.id}
                        className="text-white hover:bg-slate-800"
                      >
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: lottery.color }}
                          />
                          {lottery.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Period Selector */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Período de Análisis</label>
                <Select value={periodDays} onValueChange={setPeriodDays}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="7" className="text-white hover:bg-slate-800">Últimos 7 días</SelectItem>
                    <SelectItem value="30" className="text-white hover:bg-slate-800">Últimos 30 días</SelectItem>
                    <SelectItem value="90" className="text-white hover:bg-slate-800">Últimos 90 días</SelectItem>
                    <SelectItem value="0" className="text-white hover:bg-slate-800">Todos los datos (500+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Generate Button */}
              <Button 
                onClick={generatePrediction}
                disabled={loading || !selectedLottery}
                className="h-10 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold"
                data-testid="generate-prediction-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 size={18} className="animate-spin" />
                    Analizando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Zap size={18} />
                    Generar Predicción
                  </span>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <Card className="glass-card border-purple-500/30 mb-6 overflow-hidden">
            <CardContent className="p-8 text-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-purple-600/20 animate-pulse" />
                <Brain size={64} className="mx-auto text-purple-400 animate-bounce mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Sócrates Analizando...</h3>
                <p className="text-slate-400 mb-4">
                  Procesando datos históricos y patrones con Sócrates
                </p>
                <div className="max-w-md mx-auto">
                  <Progress value={66} className="h-2 bg-slate-800" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Prediction Result */}
        {prediction && !loading && (
          <div className="space-y-6 mb-8" data-testid="prediction-result">
            <PredictionResultCard prediction={prediction} />
            <AnalysisCards prediction={prediction} />
            <MetricsGrid prediction={prediction} />
          </div>
        )}

        {/* Prediction History */}
        <PredictionHistoryList history={history} lotteries={lotteries} loadingHistory={loadingHistory} />
      </main>

      <DisclaimerBanner variant="fixed" />
      <PredictionPaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        onPaymentComplete={() => { setShowPayment(false); generatePrediction(); }}
      />
    </div>
  );
};

export default SuperIA;
